// frontend/src/hooks/usePWA.js
import { useState, useEffect, useCallback } from 'react';
import { notificationsApi } from '../services/api';
import toast from 'react-hot-toast';

// ─── PWA Install Prompt ──────────────────────────
export const usePWAInstall = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsInstallable(false);
      toast.success('StayNivo installed successfully!');
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = useCallback(async () => {
    if (!installPrompt) return;
    const result = await installPrompt.prompt();
    if (result.outcome === 'accepted') {
      setInstallPrompt(null);
      setIsInstallable(false);
    }
    return result.outcome;
  }, [installPrompt]);

  return { isInstallable, isInstalled, install };
};

// ─── Service Worker Registration ─────────────────
export const useServiceWorker = () => {
  const [swRegistration, setSwRegistration] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Capture BEFORE registration: if null, this is a first install.
    // On first install, clients.claim() fires controllerchange with no prior SW,
    // which would cause a spurious reload and make the splash screen play twice.
    const wasControlled = !!navigator.serviceWorker.controller;

    navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then((reg) => {
        setSwRegistration(reg);
        console.log('✅ Service Worker registered');

        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setUpdateAvailable(true);
            }
          });
        });
      })
      .catch(err => console.error('SW registration failed:', err));

    // Only reload when a NEW SW version takes over an already-controlled page (SW update).
    // Skip on first install: no prior controller → no reload needed.
    if (wasControlled) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    }

    // Listen for messages from SW
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'NAVIGATE') {
        window.location.href = event.data.url;
      }
    });
  }, []);

  const applyUpdate = (reg) => {
    if (reg?.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  return { swRegistration, updateAvailable, applyUpdate: () => applyUpdate(swRegistration) };
};

// ─── Web Push Notifications ───────────────────────
export const usePushNotifications = () => {
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'denied');
  const [isSubscribed, setIsSubscribed] = useState(false);
  // true once the async getSubscription() check has resolved
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
  };

  // Core subscribe logic. silent=true skips toasts (used for auto re-subscribe on load).
  const doSubscribe = useCallback(async (silent = false) => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      if (!silent) toast.error('Push notifications are not supported in this browser');
      return;
    }
    try {
      if (typeof Notification === 'undefined') { if (!silent) toast.error('Notifications not supported'); return; }
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        if (!silent) toast.error('Notification permission denied');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const vapidRes = await notificationsApi.vapidKey();
      const vapidKey = vapidRes.data.data.publicKey;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      const subData = subscription.toJSON();
      await notificationsApi.subscribe({
        endpoint: subData.endpoint,
        p256dh: subData.keys.p256dh,
        auth: subData.keys.auth,
        userAgent: navigator.userAgent,
        lang: localStorage.getItem('staynivo-lang') || 'en',
      });
      setIsSubscribed(true);
      if (!silent) toast.success('Push notifications enabled!');
    } catch (err) {
      console.error('Push subscription failed:', err);
      if (!silent) toast.error(`Push failed: ${err?.message || err}`);
    }
  }, []);

  // Public subscribe (called by user action — always shows toasts)
  const subscribe = useCallback(() => doSubscribe(false), [doSubscribe]);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        notificationsApi.unsubscribe({ endpoint }).catch(() => {});
      }
      setIsSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (err) {
      toast.error('Failed to disable notifications');
    }
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSubscriptionChecked(true);
      return;
    }
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        if (sub) {
          setIsSubscribed(true);
        } else {
          // No active browser subscription — clear stale DB entries silently
          notificationsApi.unsubscribe({}).catch(() => {});
          // If permission was already granted, silently re-subscribe
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            doSubscribe(true);
          }
        }
        setSubscriptionChecked(true);
      });
    });
  }, [doSubscribe]);

  return { permission, isSubscribed, subscriptionChecked, subscribe, unsubscribe };
};

// ─── Socket.io Real-time ──────────────────────────
export const useSocket = (hotelId) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!hotelId) return;

    const { io } = require('socket.io-client');
    const socketUrl = process.env.REACT_APP_SOCKET_URL || window.location.origin;
    const s = io(socketUrl, {
      transports: ['websocket'],
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    s.on('connect', () => {
      setConnected(true);
      s.emit('join-hotel', hotelId);
    });

    s.on('disconnect', () => setConnected(false));

    setSocket(s);
    return () => s.disconnect();
  }, [hotelId]);

  return { socket, connected };
};
